import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

import Button from '@Components/Button';

import './Input.custom.scss';
import './Input.theme.scss';

export default class Input extends React.PureComponent {
  constructor(props) {
    super(props);

    const { value, validateValueInput, inputProps } = props;

    this.inputRef = (inputProps && inputProps.ref) || React.createRef();

    this.state = {
      value: validateValueInput(value) ? value : '',
    };
  }

  onChangeDebounced = debounce(() => {
    const { onChange } = this.props;
    const { value } = this.state;
    onChange(value);
    // eslint-disable-next-line react/destructuring-assignment
  }, this.props.debounceDelay);

  componentDidMount() {
    const { autofocus, preSelectText } = this.props;
    if (!autofocus) {
      return;
    }
    setTimeout(() => {
      if (!this.inputRef?.current) return;

      this.inputRef?.current?.focus();
      if (preSelectText) {
        this.inputRef?.current?.select();
      }
    });
  }

  // eslint-disable-next-line consistent-return
  componentDidUpdate(prevProps, prevState) {
    const { value } = this.props;
    if (value !== prevProps.value) {
      return this.onChangeValueHandler(value);
    }

    const { value: stateValue } = this.state;
    if (stateValue !== prevState.value) {
      return this.onChangeDebounced();
    }
  }

  onChangeHandler = (e) => {
    const { value } = e.target;
    this.onChangeValueHandler(value);
  };

  onKeyDown = (e) => {
    const { onApply } = this.props;
    if (e.keyCode !== 13) {
      return;
    }

    if (onApply) {
      onApply();
    }
  };

  onChangeValueHandler(value) {
    const { validateValueInput, buildQuery } = this.props;
    if (!validateValueInput(value)) {
      return;
    }
    this.setState(
      {
        value,
      },
      () => buildQuery && buildQuery.updateInputText(value)
    );
  }

  onClearClick = () => {
    const { buildQuery, onClear } = this.props;
    this.setState(
      {
        value: '',
      },
      () => buildQuery && buildQuery.updateInputText('')
    );
    this.inputRef?.current?.focus();
    if (onClear) {
      onClear();
    }
  };

  render() {
    const {
      id,
      disabled,
      className,
      inputProps,
      placeholder,
      clearButton,
      classNameInput,
      afterLabelContent,
      validationMessage,
      isValid,
      animatedPlaceholder,
      classNamePlaceholder,
      onBlur,
      onMouseDown,
      type,
    } = this.props;
    const { value } = this.state;

    const showClearButton = clearButton && value.length !== 0;

    return (
      <div className={classnames(className, 'new-input')}>
        <div
          className={classnames('new-input-animated-wrapper', {
            'new-input-animated-wrapper-disabled': disabled,
            'new-input-animated-wrapper-with-before': animatedPlaceholder,
          })}>
          <input
            ref={this.inputRef}
            disabled={disabled}
            value={value}
            onBlur={onBlur}
            onMouseDown={onMouseDown}
            onKeyDown={this.onKeyDown}
            onChange={this.onChangeHandler}
            {...inputProps}
            type={type || 'text'}
            id={id}
            className={classnames(classNameInput, 'new-input-input', {
              'new-input-input-disabled': disabled,
              'new-input-input-with-button': clearButton,
              invalid: !!validationMessage && !isValid,
            })}
            aria-describedby="new-input-validation-message"
          />
          <label
            htmlFor={id}
            className={classnames(classNamePlaceholder, 'new-input-placeholder', {
              'new-input-placeholder-animated': animatedPlaceholder,
              'new-input-placeholder-hidden': value.length !== 0 && !animatedPlaceholder,
              'new-input-placeholder-disabled': disabled,
            })}>
            {placeholder}
          </label>
          {showClearButton && (
            <Button
              type="button"
              onClick={this.onClearClick}
              buttonConfig={{
                type: 'link',
                size: 'sm',
                ariaLabel: 'Clear input',
                iconOnly: true,
                className: 'new-input-clear-button',
                e2eName: 'clear-input',
              }}
              iconConfig={{
                name: 'delete',
                size: 'tiny',
              }}
            />
          )}
          {afterLabelContent}
        </div>
        {validationMessage && <div className="new-input-validation-message">{validationMessage}</div>}
      </div>
    );
  }
}

Input.defaultProps = {
  debounceDelay: 300,
  value: '',
  onChange: noop,
  onBlur: noop,
  onMouseDown: noop,
  validateValueInput: () => true,
  animatedPlaceholder: false,
  clearButton: true,
};

Input.propTypes = {
  buildQuery: PropTypes.object,
  id: PropTypes.string,
  inputProps: PropTypes.object,
  clearButton: PropTypes.bool,
  disabled: PropTypes.bool,
  debounceDelay: PropTypes.number,
  classNamePlaceholder: PropTypes.string,
  classNameInput: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validateValueInput: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onApply: PropTypes.func,
  onMouseDown: PropTypes.func,
  onClear: PropTypes.func,
  animatedPlaceholder: PropTypes.bool,
  autofocus: PropTypes.bool,
  preSelectText: PropTypes.bool,
  afterLabelContent: PropTypes.element,

  validationMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]), // withValidationMessage HOC
  isValid: PropTypes.bool, // withValidationMessage HOC
};
